'use client'

import { createClientLogger } from '@/lib/logger/client'

const log = createClientLogger('AuthPage')
import { Label } from '@/components/Label'
import { Input } from '@/components/Input'
import { Button } from '@/components/Button'
import { useUserContext } from '@/providers/UserProvider'
import React, { useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/navigation'
import { useForm } from 'react-hook-form'
import { Container } from '@/components/Container'
import { sendMagicLink } from '@/app/(frontend)/inloggen/actions'
import { useAuthStore } from '@/stores/authStore'
import { buildMagicLinkIntent, buildProfileSetupUrl } from '@/lib/client/authRedirect'
import { Section } from '@/payload-types'
import { RenderBlocks } from '@/blocks/RenderBlocks'

type AuthMode = 'login' | 'register'

type FormValues = {
  email: string
  password: string
}

const GoogleSignInButton = ({ onSuccess, clientId }) => {
  const buttonRef = useRef(null)

  useEffect(() => {
    // Load the Google Identity Services script
    const script = document.createElement('script')
    script.src = 'https://accounts.google.com/gsi/client'
    script.async = true
    script.defer = true
    document.body.appendChild(script)

    const handleCredentialResponse = (response) => {
      if (response.credential) {
        onSuccess(response)
      } else {
        log.error('Google Sign-In failed: No credential returned')
      }
    }

    script.onload = () => {
      try {
        const google = (window as any).google
        google.accounts.id.initialize({
          client_id: clientId,
          callback: handleCredentialResponse,
          auto_select: false,
        })

        // Render the button
        google.accounts.id.renderButton(buttonRef.current, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text: 'signin_with',
          shape: 'rectangular',
          logo_alignment: 'left',
          width: 250,
        })
      } catch (error) {
        log.error({ error }, 'Error initializing Google Sign-In')
      }
    }

    script.onerror = (error) => {
      log.error({ error }, 'Error loading Google Sign-In script')
    }

    return () => {
      // Cleanup
      document.body.removeChild(script)
      try {
        const google = (window as any).google
        google?.accounts?.id?.cancel()
      } catch (error) {
        log.error({ error }, 'Error during Google Sign-In cleanup')
      }
    }
  }, [clientId, onSuccess])

  return <div ref={buttonRef}></div>
}

interface AuthPageClientProps {
  mode: AuthMode
  prefillEmail?: string
  redirectPath?: string
  flags: {
    magicLinks: boolean
    googleSignIn: boolean
  }
  googleClientId: string
  ctaText?: string
  intent?: string
  headerSection?: Section
}

export default function AuthPageClient({
  mode: initialMode,
  prefillEmail,
  redirectPath,
  flags,
  googleClientId,
  ctaText,
  intent,
  headerSection,
}: AuthPageClientProps) {
  const { signIn, refreshUser, user } = useUserContext()
  const router = useRouter()
  const [mode, setMode] = useState<AuthMode>(initialMode)
  const [message, setMessage] = useState('')
  const [isLoadingMagicLink, setIsLoadingMagicLink] = useState(false)
  const { consumeRedirect, setPendingRedirect } = useAuthStore()
  const { register, handleSubmit } = useForm({
    defaultValues: {
      email: prefillEmail || '',
    },
  })

  // Update mode if initialMode prop changes (e.g., direct navigation)
  useEffect(() => {
    setMode(initialMode)
  }, [initialMode])

  // Function to switch tabs seamlessly without reload
  const switchTab = (newMode: AuthMode) => {
    setMode(newMode)
    // Update URL without triggering a reload, preserving query parameters
    const basePath = newMode === 'login' ? '/inloggen' : '/registreren'
    const searchParams = new URLSearchParams(window.location.search)
    const queryString = searchParams.toString()
    const newPath = queryString ? `${basePath}?${queryString}` : basePath
    router.replace(newPath, { scroll: false })
  }

  // Mirror the current URL's intent into the store, clearing any stale value
  // from a previous abandoned intent flow when the user arrives without one.
  // Unlike AuthForm this runs unconditionally: this page is the dedicated auth
  // route, so already-authenticated users are expected to be redirected by the
  // consumeRedirect effect below. React runs effects in declaration order, so
  // this mirror fires before consumeRedirect and the value read downstream is
  // always the one reflecting the current URL.
  useEffect(() => {
    setPendingRedirect(redirectPath ?? null)
  }, [redirectPath, setPendingRedirect])

  // Check for error parameters in URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const error = urlParams.get('error')

    if (error === 'verification-failed') {
      setMessage('Je inloglink was ongeldig of verlopen. Probeer een nieuwe aan te vragen.')
    }
  }, [])

  // Automatically redirect authenticated users if there's a pending redirect
  useEffect(() => {
    if (user) {
      const redirect = consumeRedirect()
      if (redirect) {
        router.push(redirect)
      }
    }
  }, [user, router, consumeRedirect])

  const onSubmit = async (data) => {
    setMessage('')
    const user = await signIn(data.email, data.password)
    if (!user) return

    // Check for pending redirect from store
    const pendingRedirect = consumeRedirect()

    if (!user.profile) {
      // Forward the redirect through the profile-setup URL so it survives the
      // detour — the setup page re-stores it in Zustand on mount.
      router.push(buildProfileSetupUrl(pendingRedirect))
    } else if (pendingRedirect) {
      router.push(pendingRedirect)
    } else {
      router.push('/app')
    }
  }

  const onGoogleSignIn = async (details) => {
    setMessage('')
    if (details.credential) {
      const response = await fetch('/api/auth-google', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/text',
        },
        body: details.credential,
      })
      const result = await response.json()
      if (result.success) {
        // Refresh user context after successful OAuth login
        await refreshUser()

        // Check for pending redirect from store
        const pendingRedirect = consumeRedirect()

        // Prioritize intent redirect first, then check profile status
        if (pendingRedirect) {
          router.push(pendingRedirect)
        } else if (!user?.profile) {
          // User needs profile setup
          router.push('/mijn/profiel/aanmaken')
        } else {
          router.push('/app')
        }
      } else {
        setMessage(result.error || 'Inloggen mislukt')
      }
    } else {
      setMessage('Inloggen mislukt')
    }
  }

  const handleMagicLink = async (email: string) => {
    if (!email) {
      setMessage('Voer een e-mailadres in')
      return
    }

    setMessage('')
    setIsLoadingMagicLink(true)

    try {
      // Read `redirectPath` (the URL-derived prop) instead of consuming Zustand
      // here: the user stays on this page while the email is in flight, and the
      // server-side verify route needs the URL baked into the magic-token's
      // intent — `consumeRedirect()` would drain the store too early. Zustand
      // and `redirectPath` are kept in sync by the mount effect upstream.
      const result = await sendMagicLink(email, buildMagicLinkIntent(intent, redirectPath))

      if (result.success) {
        // Check if this is dev mode with immediate redirect
        if (result.devMode && result.magicLink) {
          setMessage('🚀 Development mode: Redirecting...')
          // Redirect immediately to the magic link for dev emails
          window.location.href = result.magicLink
        } else {
          setMessage(
            mode === 'login'
              ? '✅ Inloglink verzonden! Check je e-mail.'
              : '✅ Registratielink verzonden! Check je e-mail.',
          )
        }
      } else {
        setMessage(result.error || 'Er ging iets mis bij het verzenden van de link')
      }
    } catch (error) {
      setMessage('Er ging iets mis. Probeer het opnieuw.')
    } finally {
      setIsLoadingMagicLink(false)
    }
  }

  const isLogin = mode === 'login'

  return (
    <>
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-primary to-accent text-white py-20">
        <Container>
          <div className="text-center max-w-2xl mx-auto">
            <h1 className="text-4xl lg:text-5xl font-bold mb-6">🎬 Welkom bij JeugdFilms</h1>
            <p className="text-xl lg:text-2xl text-white/90 leading-relaxed">
              {ctaText ||
                (isLogin
                  ? 'Kies je favoriete manier om in te loggen en ga verder waar je gebleven was.'
                  : 'Kies je favoriete manier om een account aan te maken en word onderdeel van onze creatieve community.')}
            </p>
          </div>
        </Container>
      </div>

      {/* Auth Form */}
      <Container className="py-16">
        {headerSection && <RenderBlocks blocks={headerSection.blocks}/>}
        <div className="max-w-md mx-auto">
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-soft border border-white/20 p-8">
            {/* Tabs */}
            <div className="flex gap-2 mb-8 p-1 bg-slate-100 rounded-lg">
              <button
                onClick={() => switchTab('login')}
                className={`flex-1 text-center py-2 px-4 rounded-md font-medium transition-colors ${
                  isLogin
                    ? 'bg-white text-ocean-blue shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                Inloggen
              </button>
              <button
                onClick={() => switchTab('register')}
                className={`flex-1 text-center py-2 px-4 rounded-md font-medium transition-colors ${
                  !isLogin
                    ? 'bg-white text-ocean-blue shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                Registreren
              </button>
            </div>

            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {isLogin ? 'Kies je inlogmethode' : 'Kies je registratiemethode'}
              </h2>
              <p className="text-gray-600">
                {isLogin
                  ? 'Veilig en eenvoudig inloggen zonder wachtwoord'
                  : 'Veilig en eenvoudig registreren zonder wachtwoord'}
              </p>
            </div>

            {/* Authentication Options */}
            <div className="space-y-4">
              {/* Magic Link Authentication */}
              {flags.magicLinks && (
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="email" className="text-sm font-medium text-gray-700 mb-2 block">
                      E-mailadres
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      {...register('email')}
                      className="w-full"
                      placeholder="jouw@email.nl"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleSubmit((data) => handleMagicLink(data.email))()
                        }
                      }}
                    />
                  </div>

                  {message && (
                    <div
                      className={`rounded-lg p-3 text-sm ${
                        message.startsWith('✅') || message.startsWith('🚀')
                          ? 'bg-green-50 border border-green-200 text-green-700'
                          : 'bg-red-50 border border-red-200 text-red-700'
                      }`}
                    >
                      {message}
                    </div>
                  )}

                  <Button
                    onClick={handleSubmit((data) => handleMagicLink(data.email))}
                    className="w-full"
                    variant="default"
                    disabled={isLoadingMagicLink}
                  >
                    {isLoadingMagicLink
                      ? '⏳ Bezig...'
                      : isLogin
                        ? '📧 Verstuur inloglink'
                        : '📧 Verstuur registratielink'}
                  </Button>
                  <p className="text-xs text-gray-500 text-center">
                    {isLogin
                      ? 'We sturen je een veilige link om in te loggen'
                      : 'We sturen je een veilige link om je account aan te maken en direct in te loggen'}
                  </p>
                </div>
              )}

              {/* Divider */}
              {flags.magicLinks && flags.googleSignIn && (
                <div className="flex items-center">
                  <div className="flex-1 border-t border-gray-200"></div>
                  <span className="px-4 text-sm text-gray-500">of</span>
                  <div className="flex-1 border-t border-gray-200"></div>
                </div>
              )}

              {/* Google Sign In */}
              {flags.googleSignIn && (
                <div className="space-y-2">
                  <div className="flex justify-center">
                    <GoogleSignInButton clientId={googleClientId} onSuccess={onGoogleSignIn} />
                  </div>
                  <p className="text-xs text-gray-500 text-center">
                    {isLogin
                      ? 'Gebruik je Google account om snel in te loggen'
                      : 'Gebruik je Google account om snel te registreren'}
                  </p>
                </div>
              )}
            </div>

            {/* Terms Notice (only for registration) */}
            {!isLogin && (
              <div className="mt-6 text-center">
                <p className="text-xs text-gray-500">
                  Door een account aan te maken ga je akkoord met onze{' '}
                  <a
                    href="/docs/Privacyverklaring.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    privacyverklaring
                  </a>{' '}
                  en{' '}
                  <a
                    href="/docs/Algemene-voorwaarden.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    algemene voorwaarden
                  </a>
                </p>
              </div>
            )}
          </div>
        </div>
      </Container>
    </>
  )
}
